import { useEffect, useMemo } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Dialog, DialogActions, DialogContent, Grid, TextField, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { ControlledTextField, LoadingBackdrop } from 'components/common'
import { FplDialogTitle } from 'components/common/Dialog'
import { ValidationMessages } from 'constants/index'
import { useGetAppSettingByIdQuery, useUpdateAppSettingMutation } from 'generated/graphql'
import { useConfirmDialog } from 'providers'

interface IProps {
  selectedAppSettingId: number | null
  openDialog: boolean
  handleCloseDialog: () => void
}

interface IFormValues {
  value: string
}

yup.setLocale(ValidationMessages)

// Per-key validation rules. Add an entry here when a new app setting needs custom rules.
const appSettingValueRules: Record<string, yup.StringSchema> = {
  'Geofence.LocationRadius': yup
    .string()
    .required()
    .test('non-negative', 'Value cannot be negative.', (value) => Number(value) >= 0),
}

const getSchema = (key: string | undefined) =>
  yup.object().shape({
    value: (key && appSettingValueRules[key]) || yup.string().required(),
  })

const ViewAppSetting = (props: IProps) => {
  const { selectedAppSettingId, openDialog, handleCloseDialog } = props

  const { loading: retrieving, data } = useGetAppSettingByIdQuery({
    variables: { id: selectedAppSettingId! },
    skip: !selectedAppSettingId,
  })

  const appSettingKey = data?.appSetting?.key
  const schema = useMemo(() => getSchema(appSettingKey), [appSettingKey])

  const methods = useForm<IFormValues>({
    shouldUnregister: true,
    defaultValues: { value: '' },
    resolver: yupResolver(schema),
  })

  const {
    reset,
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = methods

  useEffect(() => {
    if (data?.appSetting) {
      reset({ value: data.appSetting.value })
    }
  }, [data, reset])

  const [updateAppSetting, { loading: updating }] = useUpdateAppSettingMutation({
    refetchQueries: ['GetPagedAppSettings'],
    onCompleted: () => {
      toast.success('Global variable updated successfully.')
      handleCloseDialog()
    },
  })

  const { confirmOpen } = useConfirmDialog({
    onConfirmApprove: handleCloseDialog,
  })

  const handleOnCancel = () => {
    if (isDirty) {
      confirmOpen()
    } else {
      handleCloseDialog()
    }
  }

  const onSubmit: SubmitHandler<IFormValues> = ({ value }) => {
    if (!data?.appSetting?.key) {
      toast.error('App setting key is missing.')
      return
    }

    updateAppSetting({
      variables: {
        key: data.appSetting.key,
        value,
      }
    })
  }

  const appSetting = data?.appSetting

  return (
    <Dialog fullWidth maxWidth='md' open={openDialog} aria-labelledby='view-app-setting-dialog-title' >
      <FplDialogTitle id='view-app-setting-dialog-title' onClose={handleOnCancel}>
        <Typography variant='h4' component='span'>Update Global Variable</Typography>
      </FplDialogTitle>

      <DialogContent dividers>
        <LoadingBackdrop loading={retrieving || updating} />
        <FormProvider {...methods}>
          <Grid container spacing={3} sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Key'
                value={appSetting?.key ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Name'
                value={appSetting?.displayName ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <ControlledTextField
                name='value'
                label='Value'
                control={control}
                required
                error={!!errors.value}
                helperText={errors.value?.message}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Value Type'
                value={appSetting?.valueType ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label='Description'
                value={appSetting?.description ?? ''}
                InputProps={{ readOnly: true }}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleOnCancel}>Cancel</Button>
        <Button
          variant='contained'
          disabled={updating || retrieving}
          onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ViewAppSetting